import React, { useEffect, useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { ChevronDown, CreditCard, X } from 'lucide-react';
import { authFetch } from '../../lib/api';
import { cn } from '../../lib/utils';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// Module-level cache. Shared across all CFCardField instances for instant
// open after the first fetch.
let _cfCardsCache = null;
let _cfCardsInflight = null;

const fetchCFCards = async () => {
  if (_cfCardsCache) return _cfCardsCache;
  if (_cfCardsInflight) return _cfCardsInflight;
  _cfCardsInflight = (async () => {
    try {
      const res = await authFetch(`${API}/cf-cards`);
      if (!res.ok) return [];
      const data = await res.json();
      _cfCardsCache = data.data || [];
      return _cfCardsCache;
    } catch (_e) {
      return [];
    } finally {
      _cfCardsInflight = null;
    }
  })();
  return _cfCardsInflight;
};

export const invalidateCFCardsCache = () => {
  _cfCardsCache = null;
};

const splitTokens = (raw) => {
  if (!raw && raw !== 0) return [];
  const s = typeof raw === 'string' ? raw : String(raw);
  return s
    .split(/[,;\s]+/)
    .map((t) => t.trim())
    .filter(Boolean);
};

const joinTokens = (arr) => arr.filter(Boolean).join(', ');

/**
 * Inline field for selecting one or more CF cards allocated to a job.
 *
 * - Free-text input still works (typed digits separated by commas/spaces).
 * - Card icon opens a popover with: search, checkbox grid of every card
 *   (sorted ascending), Selected counter, Clear, Done.
 * - Multi-select; stored as comma-separated card numbers.
 *
 * Props mirror SalesField for drop-in compatibility.
 */
export const CFCardField = ({ label, field, defaultValue, onFieldChange }) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [cards, setCards] = useState(() => _cfCardsCache || []);
  const [value, setValue] = useState(() => (defaultValue == null ? '' : String(defaultValue)));

  useEffect(() => {
    setValue(defaultValue == null ? '' : String(defaultValue));
  }, [defaultValue]);

  useEffect(() => {
    if (!open) return;
    if (cards.length > 0) return;
    let cancelled = false;
    fetchCFCards().then((list) => {
      if (!cancelled) setCards(list);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectedTokens = useMemo(() => splitTokens(value), [value]);
  const selectedSet = useMemo(() => new Set(selectedTokens.map((s) => s.toLowerCase())), [selectedTokens]);

  // Tokens already saved that aren't in the canonical CF list — e.g. legacy
  // "Bag A" / "spare-3" notes from before the master list existed.
  const customTokens = useMemo(() => {
    const known = new Set((cards || []).map((c) => String(c.card_number)));
    return selectedTokens.filter((t) => !known.has(t));
  }, [cards, selectedTokens]);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return cards;
    return cards.filter((c) =>
      String(c.card_number).includes(f) ||
      (c.label || '').toLowerCase().includes(f)
    );
  }, [cards, filter]);

  const toggleCard = (cardNumber) => {
    const tok = String(cardNumber);
    const has = selectedSet.has(tok.toLowerCase());
    let nextSelected;
    if (has) {
      nextSelected = selectedTokens.filter((t) => t.toLowerCase() !== tok.toLowerCase());
    } else {
      // Keep numeric tokens sorted ascending; preserve any custom strings at the end.
      const numericTokens = [...selectedTokens.filter((t) => /^\d+$/.test(t)), tok]
        .map((t) => parseInt(t, 10))
        .sort((a, b) => a - b)
        .map(String);
      const customs = selectedTokens.filter((t) => !/^\d+$/.test(t));
      nextSelected = [...numericTokens, ...customs];
    }
    const joined = joinTokens(nextSelected);
    setValue(joined);
    onFieldChange(field, joined);
  };

  const clearAll = () => {
    setValue('');
    onFieldChange(field, '');
  };

  const handleManualChange = (e) => {
    const next = e.target.value;
    setValue(next);
    onFieldChange(field, next);
  };

  return (
    <div className="flex items-center justify-between py-1.5 border-b border-muted/50 last:border-0">
      <span className="text-sm text-muted-foreground w-44 shrink-0">{label}:</span>
      <div className="flex items-center gap-1 w-full">
        <Input
          className="h-7 text-sm"
          value={value}
          onChange={handleManualChange}
          placeholder="-"
          data-testid={`cf-card-input-${field}`}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              type="button"
              data-testid={`cf-card-picker-${field}`}
              title="Pick from CF cards list"
            >
              <CreditCard className="w-3.5 h-3.5" />
              <ChevronDown className="w-2.5 h-2.5 -ml-0.5 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 p-0" data-testid={`cf-card-popover-${field}`}>
            <div className="p-2 border-b">
              <Input
                placeholder="Search cards…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="h-8 text-sm"
                autoFocus
                data-testid={`cf-card-search-${field}`}
              />
            </div>
            <div className="max-h-64 overflow-y-auto p-1">
              {customTokens.length > 0 && (
                <div className="px-2 py-1">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                    Custom (not in list)
                  </div>
                  {customTokens.map((t) => (
                    <label key={`custom-${t}`} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-muted cursor-pointer">
                      <Checkbox checked onCheckedChange={() => toggleCard(t)} />
                      <span className="text-sm italic text-amber-800 dark:text-amber-300">{t}</span>
                    </label>
                  ))}
                </div>
              )}
              {filtered.length === 0 ? (
                <div className="px-3 py-3 text-sm text-muted-foreground text-center">
                  No CF cards match
                </div>
              ) : (
                filtered.map((c) => {
                  const checked = selectedSet.has(String(c.card_number).toLowerCase());
                  return (
                    <label
                      key={c.card_id}
                      className={cn(
                        'flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer',
                        checked && 'bg-muted'
                      )}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleCard(c.card_number)}
                        data-testid={`cf-card-checkbox-${field}-${c.card_id}`}
                      />
                      <span className="flex-1">
                        <div className="text-sm font-mono">CF {c.card_number}</div>
                        {c.label && <div className="text-[10px] text-muted-foreground">{c.label}</div>}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
            <div className="border-t p-1.5 flex items-center justify-between">
              <span className="text-[10px] text-muted-foreground pl-1">
                {selectedTokens.length} selected
              </span>
              <div className="flex items-center gap-1">
                {selectedTokens.length > 0 && (
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={clearAll} data-testid={`cf-card-clear-${field}`}>
                    <X className="w-3 h-3 mr-1" />
                    Clear
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setOpen(false)}>
                  Done
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

export default CFCardField;
